/**
 * "ALL" (week-level) checkbox — portals into FullCalendar's
 * `.fc-timegrid-axis-frame` so it sits on the same row as the day
 * checkboxes. Uses Kula Checkbox + Tooltip. Tooltip is scoped to
 * the checkbox only; the "ALL" label is not a hover target.
 *
 * Accepts the shared `DayCheckbox` enum so the UI never has to
 * reason about `isChecked + isIndeterminate + isDisabled`
 * independently — the state machine is decided in `selectors.ts`.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex } from '@chakra-ui/react';
import {
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import type { DayCheckbox } from '../state/selectors';

interface AllDaysCheckboxProps {
  hostElement: HTMLElement | null;
  state: DayCheckbox;
  disabledReason?: string;
  onToggle: (_checked: boolean) => void;
}

export function AllDaysCheckbox({
  hostElement,
  state,
  disabledReason,
  onToggle,
}: AllDaysCheckboxProps) {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!hostElement) return;
    const find = () =>
      hostElement.querySelector<HTMLElement>('.fc-timegrid-axis-frame');
    // Subscribe to FullCalendar's async axis render via
    // MutationObserver so `setTarget` only fires from a subscription
    // callback, never synchronously inside the effect body.
    const applyTarget = () => setTarget(find());
    const observer = new MutationObserver(applyTarget);
    observer.observe(hostElement, { childList: true, subtree: true });
    queueMicrotask(applyTarget);
    return () => observer.disconnect();
  }, [hostElement]);

  if (!target) return null;

  const isChecked = state === 'full';
  const isIndeterminate = state === 'partial';
  const isDisabled = state === 'disabled';

  const checkboxEl = (
    <Checkbox
      size="sm"
      checked={isChecked}
      indeterminate={isIndeterminate}
      disabled={isDisabled}
      onChange={() => {
        if (isDisabled) return;
        if (state === 'full') onToggle(false);
        else onToggle(true);
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );

  const checkboxWithTooltip =
    isDisabled && disabledReason ? (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Box as="span" display="inline-flex" lineHeight={0}>
              {checkboxEl}
            </Box>
          </TooltipTrigger>
          <TooltipContent side="right">{disabledReason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      checkboxEl
    );

  const inner = (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="4px"
      h="100%"
      w="100%"
      py="10px"
    >
      <KText
        fontSize="11px"
        fontWeight="500"
        textTransform="uppercase"
        color="secondary-500"
      >
        ALL
      </KText>
      {checkboxWithTooltip}
    </Flex>
  );

  return createPortal(inner, target);
}
